import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Trash2,
  RotateCcw,
  Trash,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";

/**
 * Trash bin for soft-deleted projects (super_admin only).
 *
 * Renders a collapsible card at the bottom of the Projects page. Each
 * row shows the project name, deletion metadata, task/list counts and
 * a countdown to auto-purge. Actions: Restore or "Kalıcı sil".
 *
 * `refreshKey` forces a reload when the parent (Projects.jsx) mutates
 * the trash (e.g. after a soft-delete). `onRestored` bubbles up so the
 * parent can refresh its main projects grid.
 */
export default function TrashBin({ refreshKey, onRestored }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null); // project id currently mutating
  const [confirm, setConfirm] = useState(null); // {id,name} or null

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects/trash");
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Auto-expand the panel when new items appear so the user
  // immediately sees what they just deleted.
  useEffect(() => {
    if (items.length > 0) setOpen(true);
  }, [items.length]);

  const restore = async (project) => {
    setBusy(project.id);
    try {
      await api.post(`/projects/${project.id}/restore`);
      toast.success(t("trash.restore_success", { name: project.name }));
      window.dispatchEvent(new Event("serpix:projects-changed"));
      await load();
      onRestored?.();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          t("trash.restore_error"),
      );
    } finally {
      setBusy(null);
    }
  };

  const permanentDelete = async () => {
    if (!confirm) return;
    setBusy(confirm.id);
    try {
      await api.delete(`/projects/${confirm.id}/permanent`);
      toast.success(
        t("trash.permanent_delete_success", { name: confirm.name }),
      );
      setConfirm(null);
      await load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          t("trash.delete_error"),
      );
    } finally {
      setBusy(null);
    }
  };

  const fmtCountdown = (purge_at) => {
    if (!purge_at) return "—";
    const ms = new Date(purge_at).getTime() - Date.now();
    if (ms <= 0) return "0h";
    const days = Math.floor(ms / (24 * 3600_000));
    if (days >= 1) return t("trash.days_left_one", { count: days });
    const hours = Math.max(1, Math.floor(ms / 3600_000));
    return t("trash.hours_left", { count: hours });
  };

  // Hide entirely when the trash is empty AND the panel has never been
  // opened — no visual noise for tenants that don't use the feature.
  if (items.length === 0 && !open && !loading) {
    return null;
  }

  return (
    <div
      data-testid="trash-bin-card"
      className="rounded-xl border border-zinc-200 bg-white"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="trash-bin-toggle"
        className="flex w-full items-center gap-3 rounded-t-xl px-5 py-4 text-left hover:bg-zinc-50"
      >
        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-zinc-100">
          <Trash2 className="h-4 w-4 text-zinc-500" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-zinc-900">
              {t("trash.title")}
            </span>
            {items.length > 0 && (
              <span
                className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-zinc-900 px-1.5 text-[10px] font-semibold text-white"
                data-testid="trash-bin-count"
              >
                {items.length}
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-zinc-500">
            {t("trash.subtitle")}
          </div>
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-zinc-400" />
        ) : (
          <ChevronRight className="h-4 w-4 text-zinc-400" />
        )}
      </button>

      {open && (
        <div className="border-t border-zinc-100 px-5 py-4">
          {loading && items.length === 0 && (
            <p className="py-6 text-center text-xs text-zinc-400">
              {t("common.loading")}
            </p>
          )}
          {!loading && items.length === 0 && (
            <div
              className="rounded-lg border border-dashed border-zinc-200 p-8 text-center"
              data-testid="trash-bin-empty"
            >
              <Trash className="mx-auto mb-2 h-6 w-6 text-zinc-300" />
              <p className="text-sm font-medium text-zinc-700">
                {t("trash.empty")}
              </p>
              <p className="mt-1 text-xs text-zinc-500">
                {t("trash.empty_hint")}
              </p>
            </div>
          )}
          {items.length > 0 && (
            <ul className="divide-y divide-zinc-100">
              {items.map((p) => {
                const deletedOn = p.deleted_at
                  ? new Date(p.deleted_at).toLocaleDateString(
                      i18n.language === "en" ? "en-US" : "tr-TR",
                      { day: "2-digit", month: "short", year: "numeric" },
                    )
                  : "—";
                return (
                  <li
                    key={p.id}
                    data-testid={`trash-item-${p.id}`}
                    className="flex flex-wrap items-center gap-3 py-3"
                  >
                    <span
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ background: p.color || "#0055FF" }}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium text-zinc-900">
                        {p.name}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500">
                        <span>{t("trash.deleted_on")}: {deletedOn}</span>
                        <span className="text-zinc-300">·</span>
                        <span>
                          {p.task_count || 0} {t("trash.tasks")} · {p.list_count || 0}{" "}
                          {t("trash.lists")}
                        </span>
                        <span className="text-zinc-300">·</span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                          <AlertTriangle className="h-3 w-3" />
                          {fmtCountdown(p.purge_at)}
                        </span>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => restore(p)}
                        disabled={busy === p.id}
                        data-testid={`trash-restore-${p.id}`}
                        className="gap-1.5"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        {busy === p.id ? t("trash.restoring") : t("trash.restore")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setConfirm({ id: p.id, name: p.name })
                        }
                        disabled={busy === p.id}
                        data-testid={`trash-permanent-${p.id}`}
                        className="gap-1.5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash className="h-3.5 w-3.5" />
                        {t("trash.permanent_delete")}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <AlertDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
      >
        <AlertDialogContent data-testid="trash-permanent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("trash.permanent_delete_confirm_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("trash.permanent_delete_confirm_desc", {
                name: confirm?.name || "",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trash-permanent-cancel">
              {t("trash.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                permanentDelete();
              }}
              data-testid="trash-permanent-confirm"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t("trash.permanent_delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
